<script lang="ts" setup>
import { ClockIcon } from "lucide-vue-next";
import { ref, computed, watch, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
  seconds: number;
  message?: string;
}>();

// Callers can override the default "too many requests" line with context-specific text.
const displayMessage = computed(() => props.message || t("msg.tooManyRequests"));

const emit = defineEmits<{
  (e: "expired"): void;
}>();

const countdown = ref(props.seconds);
let interval: ReturnType<typeof setInterval> | null = null;

const formattedTime = computed(() => {
  const total = countdown.value;
  const mins = Math.floor(total / 60);
  const secs = total % 60;

  if (mins > 0) {
    const minLabel = mins === 1 ? t("msg.minute") : t("msg.minutes");
    const secLabel = secs === 1 ? t("msg.second") : t("msg.seconds");
    return `${mins} ${minLabel} ${secs} ${secLabel}`;
  }
  return `${secs} ${secs === 1 ? t("msg.second") : t("msg.seconds")}`;
});

function startCountdown(seconds: number) {
  countdown.value = seconds;

  if (interval) {
    clearInterval(interval);
  }

  interval = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
      emit("expired");
    }
  }, 1000);
}

// Watch for changes to seconds prop to restart countdown
watch(
  () => props.seconds,
  (newSeconds) => {
    if (newSeconds > 0) {
      startCountdown(newSeconds);
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  if (interval) {
    clearInterval(interval);
  }
});
</script>

<template>
  <Transition name="rate-limit-fade">
    <div
      v-if="countdown > 0"
      class="flex flex-col items-center gap-3 rounded-lg border border-warning-fill/30 bg-warning-fill/5 p-4 text-center"
    >
      <div
        class="flex h-10 w-10 items-center justify-center rounded-full border border-warning-fill/40 bg-warning-fill/15"
      >
        <ClockIcon class="size-5 text-warning" aria-hidden="true" />
      </div>

      <p class="text-sm font-medium text-warning">
        {{ displayMessage }}
      </p>

      <p class="text-base font-semibold text-warning tabular-nums">
        {{ formattedTime }}
      </p>
    </div>
  </Transition>
</template>

<style scoped>
.rate-limit-fade-enter-active,
.rate-limit-fade-leave-active {
  transition: opacity 0.3s ease;
}
.rate-limit-fade-enter-from,
.rate-limit-fade-leave-to {
  opacity: 0;
}
</style>
